import { useState } from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { type AgentInboxMessage } from '@/backendApis';
import CopyButton from '@/components/blocks/CopyButton';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/EmptyState';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Skeleton } from '@/components/ui/skeleton';

import { AgentInboxDialogHeader } from './agent-inbox-dialog-header';
import { AgentInboxMessageRow } from './agent-inbox-message-row';
import {
  useAgentInboxAddressQuery,
  useAgentInboxMessagesQuery,
} from './agent-inbox.query';

type Folder = 'inbox' | 'held' | 'sent';

type FolderEmptyState = {
  icon: LBIcon;
  title: string;
  description: string;
};

const EMPTY_STATES: Record<Folder, FolderEmptyState> = {
  inbox: {
    icon: Icons.inbox,
    title: 'No mail yet',
    description:
      'Email your assistant from your own address. Sign-up codes it waits for land here too.',
  },
  held: {
    icon: Icons.shield,
    title: 'Nothing held',
    description:
      "Mail from people you didn't copy your assistant with waits here. Your assistant never reads or acts on it.",
  },
  sent: {
    icon: Icons.send,
    title: 'No replies yet',
    description: 'Replies your assistant sends show up here.',
  },
};

type AgentInboxMailboxProps = {
  onOpenMessage: (messageId: string) => void;
  onChangeAddress: () => void;
  onOpenContacts: () => void;
};

export const AgentInboxMailbox = ({
  onOpenMessage,
  onChangeAddress,
  onOpenContacts,
}: AgentInboxMailboxProps) => {
  const [folder, setFolder] = useState<Folder>('inbox');
  const received = useAgentInboxMessagesQuery(false);
  const sent = useAgentInboxMessagesQuery(true);
  const query = folder === 'sent' ? sent : received;
  const receivedMessages = received.data ?? [];
  const heldCount = receivedMessages.filter((m) => m.kind === 'held').length;
  const messages =
    folder === 'sent'
      ? (sent.data ?? [])
      : receivedMessages.filter(
          (m) => (m.kind === 'held') === (folder === 'held')
        );

  return (
    <>
      <AgentInboxDialogHeader
        title="Assistant email"
        description="Email your assistant from your own address and it replies like it does in chat."
      />
      <YStack className="min-h-0 flex-1 gap-5 px-6 pb-6">
        <YStack className="gap-2">
          <AddressCard onChangeAddress={onChangeAddress} />
          <XStack className="items-center gap-2 rounded-lg border border-border bg-surface py-3 pl-4 pr-3">
            <YStack className="min-w-0 flex-1 gap-1">
              <span className="text-xs font-medium text-foreground-muted">
                Contacts
              </span>
              <span className="truncate text-sm text-foreground">
                Choose who gets direct replies
              </span>
            </YStack>
            <Button variant="outline" size="sm" onClick={onOpenContacts}>
              Manage
            </Button>
          </XStack>
        </YStack>
        <YStack className="min-h-0 flex-1 gap-3">
          <SegmentedControl.Root
            value={folder}
            onValueChange={(value) => setFolder(value as Folder)}
          >
            <SegmentedControl.List className="w-full">
              <SegmentedControl.Trigger value="inbox" className="flex-1">
                Inbox
              </SegmentedControl.Trigger>
              <SegmentedControl.Trigger value="held" className="flex-1">
                Held{heldCount > 0 && ` · ${heldCount}`}
              </SegmentedControl.Trigger>
              <SegmentedControl.Trigger value="sent" className="flex-1">
                Sent
              </SegmentedControl.Trigger>
            </SegmentedControl.List>
          </SegmentedControl.Root>
          <MessageList
            folder={folder}
            messages={messages}
            isLoading={query.isLoading}
            isError={query.isError}
            onRetry={() => void query.refetch()}
            onOpenMessage={onOpenMessage}
          />
        </YStack>
      </YStack>
    </>
  );
};

const AddressCard = ({ onChangeAddress }: { onChangeAddress: () => void }) => {
  const { data: address, isError } = useAgentInboxAddressQuery();
  return (
    <XStack className="items-center gap-2 rounded-lg border border-border bg-surface py-3 pl-4 pr-3">
      <YStack className="min-w-0 flex-1 gap-1">
        <span className="text-xs font-medium text-foreground-muted">
          Your assistant&apos;s address
        </span>
        {address ? (
          <span className="truncate font-mono text-sm text-foreground">
            {address}
          </span>
        ) : isError ? (
          <span className="text-sm text-danger-11">
            Couldn&apos;t load the address
          </span>
        ) : (
          <Skeleton className="h-5 w-56" />
        )}
      </YStack>
      {address && (
        <CopyButton
          text={address}
          copyText="Copy address"
          aria-label="Copy address"
          size="sm"
        />
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={!address}
        onClick={onChangeAddress}
      >
        Change
      </Button>
    </XStack>
  );
};

type MessageListProps = {
  folder: Folder;
  messages: AgentInboxMessage[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onOpenMessage: (messageId: string) => void;
};

const MessageList = ({
  folder,
  messages,
  isLoading,
  isError,
  onRetry,
  onOpenMessage,
}: MessageListProps) => {
  if (isLoading) {
    return (
      <YStack className="gap-1" aria-busy>
        {[0, 1, 2].map((index) => (
          <XStack key={index} className="items-start gap-3 px-3 py-2.5">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <YStack className="flex-1 gap-2 pt-0.5">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3.5 w-64" />
            </YStack>
          </XStack>
        ))}
      </YStack>
    );
  }

  if (isError) {
    return (
      <YStack center className="flex-1 gap-3 text-center">
        <p role="alert" className="text-sm text-danger-11">
          Couldn&apos;t load your mailbox.
        </p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </YStack>
    );
  }

  if (messages.length === 0) {
    const empty = EMPTY_STATES[folder];
    return (
      <Empty.Root variant="compact" className="flex-1">
        <Empty.Media variant="icon">
          <empty.icon />
        </Empty.Media>
        <Empty.Header>
          <Empty.Title>{empty.title}</Empty.Title>
          <Empty.Description>{empty.description}</Empty.Description>
        </Empty.Header>
      </Empty.Root>
    );
  }

  return (
    <ScrollArea className="-mx-3 min-h-0 flex-1">
      <YStack className="gap-0.5 px-3">
        {messages.map((message) => (
          <AgentInboxMessageRow
            key={message.id}
            message={message}
            onOpen={() => onOpenMessage(message.id)}
          />
        ))}
      </YStack>
    </ScrollArea>
  );
};
